import { action, computed, observable } from 'mobx';
import { capitalize, get } from 'lodash';
import moment from 'moment';
import { ALERT_API_STATES } from '@kentik/ui-shared/alerting/constants';
import { ALERTING_TYPES, MINUTES_PER_TRIGGER_BATCH } from '@kentik/ui-shared/synthetics/constants';
import {
  getFirstTriggerTime,
  getChartTimeRange,
  getMaxRawLookbackByTestFreq
} from '@kentik/ui-shared/synthetics/utils';
import { Collection } from 'core/model';
import { renderTestResultsLink } from 'app/views/synthetics/utils/alarmsTableRenderers';
import AlarmTriggerModel from './AlarmTriggerModel';

export default class AlarmTriggerCollection extends Collection {
  sortState = { field: 'timestamp', direction: 'asc' };

  // defines a start/end time used for fetching batches of triggers
  nextFetchTimeRange = null;

  alarmId = null;

  // helps to make sure we don't fetch for more triggers when we're in the middle of comparing selections
  @observable
  isComparingSelected = false;

  // loading indicator when fetching trigger batches
  @observable
  loadingTriggers = false;

  @observable.ref
  alarm = null;

  @observable.ref
  triggerBatch = null;

  get model() {
    return AlarmTriggerModel;
  }

  get url() {
    return `/api/ui/synthetics/alarms/details/${this.alarmId}`;
  }

  @computed
  get metric() {
    return get(this.alarm, 'key.value.metric', null);
  }

  @computed
  get alarmState() {
    return get(this.alarm, 'state', null);
  }

  @computed
  get severity() {
    return get(this.alarm, 'severity', null);
  }

  @computed
  get alarmFirstTrigger() {
    // fall back to alarm start in case this is an alarm that was pre-first trigger decorated
    return getFirstTriggerTime(get(this.alarm, 'event_start_time', null) || this.alarmStart);
  }

  @computed
  get alarmFirstTriggerTime() {
    return (this.alarmFirstTrigger && moment(this.alarmFirstTrigger).unix()) || null;
  }

  // Not @computed — intentionally recalculated on each access so `now` is always fresh.
  // This matters for active alarms where end_time = now, and for expanding additional chart rows.
  get chartTimeRange() {
    const maxRawLookback = this.test ? getMaxRawLookbackByTestFreq(this.test) : null;

    // fallback to the 60s-test budget (1hr) when test is missing or has no frequency config
    const effectiveMaxRawLookback = maxRawLookback || 3600;

    return getChartTimeRange({
      maxRawLookback: effectiveMaxRawLookback,
      alarmFirstTriggerTime: this.alarmFirstTriggerTime,
      alarmEndTime: this.alarmState === ALERT_API_STATES.CLEAR ? this.alarmEndTime : null,
      now: moment().unix()
    });
  }

  // backward-compatible — existing code references testResultsStartTime
  get testResultsStartTime() {
    return this.chartTimeRange.start_time;
  }

  // chart end time (distinct from alarmEndTime which is alarm-only)
  get chartEndTime() {
    return this.chartTimeRange.end_time;
  }

  @computed
  get alarmStart() {
    return get(this.alarm, 'start_time', null);
  }

  @computed
  get alarmStartTime() {
    return (this.alarmStart && moment(this.alarmStart).unix()) || null;
  }

  @computed
  get alarmEnd() {
    if (this.alarmState === ALERT_API_STATES.CLEAR) {
      return get(this.alarm, 'end_time', null);
    }

    return moment().toISOString();
  }

  @computed
  get alarmEndTime() {
    return moment(this.alarmEnd).unix();
  }

  @computed
  get alarmGroup() {
    return {
      name: get(this.alarm, 'key.value.group', '').split(':')[0], // group type is the first half of the key
      data: get(this.alarm, 'details.groupData', null)
    };
  }

  @computed
  get alarmGroupLabel() {
    return capitalize(this.alarmGroup.name);
  }

  @computed
  get testId() {
    return get(this.alarm, 'key.value.testId', null);
  }

  @computed
  get test() {
    return this.store.$syn.getTest(this.testId);
  }

  @computed
  get alertingType() {
    return this.test?.alertingType || ALERTING_TYPES.AGENT;
  }

  @computed
  get testResultsLink() {
    return renderTestResultsLink({
      $syn: this.store.$syn,
      model: this.alarm,
      startTime: this.alarmFirstTrigger,
      dateRangeOnly: true,
      raw: true
    });
  }

  @computed
  get testEditLink() {
    const { testId } = this;

    if (testId) {
      return `/v4/synthetics/tests/${this.testId}`;
    }

    return null;
  }

  @computed
  get target() {
    return get(this.alarm, 'details.target', null);
  }

  @computed
  get agentId() {
    return get(this.alarm, 'key.value.agentId', null);
  }

  @computed
  get targetAgentId() {
    return get(this.alarm, 'details.target_agent_id', null);
  }

  @computed
  get resolver() {
    return get(this.alarm, 'details.resolver', null);
  }

  @computed
  get testResultsTarget() {
    return this.resolver || this.targetAgentId || this.target;
  }

  @computed
  get hasExpandableCharts() {
    return this.get().some((model) => model.hasExpandableCharts);
  }

  @computed
  get hasResults() {
    return this.alarm && this.test;
  }

  // determines fetchability for the next batch of triggers
  // the start must be before the alarm's end
  @computed
  get canFetchMore() {
    if (!this.isComparingSelected && this.nextFetchTimeRange) {
      return moment(this.nextFetchTimeRange.start).isBefore(this.alarmEnd);
    }

    return false;
  }

  @action
  initGrouping() {
    this.group(this.alertingType === ALERTING_TYPES.AGENT ? null : 'timestamp');
  }

  @action
  setAlarm(alarm) {
    this.alarm = alarm;
  }

  @action
  reset({ alarmId = null, ...options } = {}) {
    this.alarmId = alarmId;
    this.setAlarm(null);

    super.reset(options);
  }

  // the next trigger batch window will begin at the end and extend to a constant length in minutes
  setNextFetchTimeRange({ start, end } = {}) {
    if (start && end) {
      this.nextFetchTimeRange = {
        start: end,
        end: moment(end).add(MINUTES_PER_TRIGGER_BATCH, 'minutes')
      };
    }
  }

  @action
  compareSelected(compare) {
    this.isComparingSelected = compare;

    if (compare) {
      // remove grouping when we're comparing charts, the row has the appropriate info already
      this.group(null);
      this.filter((model) => model.isSelected);
    } else {
      this.resetState();
      // put grouping back if we had it
      this.initGrouping();
    }
  }

  // use the trigger batch size to request the initial payload
  @action
  async fetch({ alarmId, force = false } = {}) {
    if (alarmId) {
      if (!force && alarmId === this.alarmId) {
        // same alarm id, noop
        return null;
      }

      // we have a new alarm id, update and fetch fresh
      this.reset({ alarmId });
      return super.fetch({ query: { pageSize: MINUTES_PER_TRIGGER_BATCH } });
    }

    // no alarm id, reset the whole collection
    return this.reset();
  }

  // fetches the next batch of triggers and adds them to the collection
  @action
  async fetchMore() {
    const { $syn } = this.store;
    const { start: nextStart, end: nextEnd } = this.nextFetchTimeRange;

    if (!this.canFetchMore) {
      return null;
    }

    this.loadingTriggers = true;

    return $syn.requests
      .fetchAlarmTriggers({
        ruleId: this.alarm?.rule_id,
        keys: this.alarm?.key?.value || {},
        start: nextStart,
        end: moment.min(moment(nextEnd), moment(this.alarmEnd)).toISOString() // smallest possible batches
      })
      .then((response) => {
        const { start, end, triggers } = response;

        // set next fetch window
        this.setNextFetchTimeRange({ start, end });

        // tack on the new triggers
        this.add(triggers);

        // update the current batch of loaded triggers so we can collapse them on-demand
        this.triggerBatch = triggers;

        return response;
      })
      .finally(action(() => (this.loadingTriggers = false)));
  }

  async fetchSyngestResults({ agentId, target }) {
    const { agentResults } = this.test;
    const { start_time, end_time } = this.chartTimeRange;

    // set up properties normally handled via the 'configure' stage
    agentResults.agent_id = agentId;
    agentResults.target = target;
    agentResults.setupAgentResults(agentId, target);

    return agentResults.fetchTestResults({ start_time, end_time }).then(() => agentResults);
  }

  deserialize(data) {
    if (data?.alarm) {
      // capture the alarm
      this.setAlarm(data.alarm);
    }

    // initialize grouping
    this.initGrouping();

    // update the next fetch time range
    this.setNextFetchTimeRange({ start: data?.start, end: data?.end });

    return super.deserialize(data?.triggers || []);
  }
}
